import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps } from '@ui/src';
import { AccountType, Total, ReportSheet, Category } from '../../../types';
import { profitLossApi } from '../services';
import { ProfitLoss } from '../../types';
import formReport from '../../../helpers/form-report';

export interface ProfitLossSheetState {
  isLoading: boolean;
  requestInProgress: boolean;
  values?: ReportSheet[];
  itemsVisible: AccountType[];
  checksum: number;
  formationTime?: {
    start?: number;
    end?: number;
    duration?: number;
  };
  lists: {
    categories: SelectItemProps[];
    groups: SelectItemProps[];
    primaries: SelectItemProps[];
  };
  profitLoss?: Total;
  totalExpenses?: Total;
  totalIncome?: Total;
  accountsCategories?: Category[];
}

const initialState: ProfitLossSheetState = {
  isLoading: false,
  values: undefined,
  requestInProgress: false,
  formationTime: undefined,
  itemsVisible: [],
  lists: {
    categories: [],
    groups: [],
    primaries: [],
  },
  checksum: 0,
};

export const profitLossSheetSlice = createSlice({
  name: 'profitLossSheet',
  initialState,
  reducers: {
    changeVisibility: (state, action: PayloadAction<AccountType[]>) => {
      state.itemsVisible = action.payload;
    },
    setPLAccountsCategories: (state, action: PayloadAction<Category[]>) => {
      state.accountsCategories = action.payload;
    },
    clearProfitLossSheetState: (state) => {
      state.isLoading = true;
      state.requestInProgress = false;
      state.values = undefined;
      state.itemsVisible = [];
      state.checksum = 0;
      state.profitLoss = undefined;
      state.totalExpenses = undefined;
      state.totalIncome = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        profitLossApi.endpoints.getProfitLossReport.matchPending,
        (state) => {
          state.formationTime = {
            start: Date.now(),
            end: undefined,
            duration: undefined,
          };
        },
      )
      .addMatcher(
        profitLossApi.endpoints.getProfitLossReport.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: ProfitLoss }> & {
            meta: { fulfilledTimeStamp: number };
          },
        ) => {
          const { checksum, profitLoss, totalExpenses, totalIncome } =
            action.payload.data;

          const accountCategories = state.accountsCategories;

          const reportResult = formReport({
            ...action.payload.data,
            accountCategories: accountCategories as Category[],
          });

          state.lists = reportResult.lists;
          state.values = reportResult.sheet;

          state.checksum = checksum || 0;
          state.profitLoss = profitLoss || undefined;
          state.totalExpenses = totalExpenses || undefined;
          state.totalIncome = totalIncome || undefined;

          state.formationTime = {
            ...state.formationTime,
            end: action.meta.fulfilledTimeStamp,
            duration: Math.round(
              (action.meta.fulfilledTimeStamp -
                (state.formationTime?.start as number)) /
                1000,
            ),
          };
        },
      );
  },
});

export const {
  changeVisibility,
  clearProfitLossSheetState,
  setPLAccountsCategories,
} = profitLossSheetSlice.actions;

export default profitLossSheetSlice.reducer;
